/* DrinksNation storefront events (S5-03; v1.1 §8). What the shopper did on the page — a product viewed,
   something added to the basket, a prompt shown or dismissed — posted to the Brain through the app proxy
   (POST /apps/dn/api/events) so the loop can read it later. Loaded from layout/theme.liquid on every page
   after dn-codes.js.
   - Fire and forget. Events queue for a moment and leave in one navigator.sendBeacon (fetch keepalive
     when there is no beacon), on a short timer and when the page is hidden or left. Nothing waits on
     the answer and nothing on the page changes when the Brain is away: every call is wrapped, every
     failure is dropped.
   - One session id per browser tab (sessionStorage), one client id per event: the Brain dedupes a
     replayed beacon on the pair, so a retry cannot double-count an add.
   - The customer is never in the body. The proxy signs who is logged in; the page does not get a say.
   - `added` carries the cart token and the drinks total from /cart.js (deposit and small-order fee
     left out), which is how a basket that keeps growing is not chased after the first add (12 Sep 2026).
   - window.dnEvents.track(type, payload) is for dn-prompt: prompt_shown, prompt_clicked, dismissed.
   Vanilla JS, no framework. The pure helpers are exported for node: qa/unit/dn-events.test.mjs. */
(function (root, factory) {
  var api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.dnEvents = api;
  if (typeof document !== 'undefined') api.boot();
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';
  var ENDPOINT = '/apps/dn/api/events';
  var SESSION_KEY = 'dn_sid';
  var FLUSH_MS = 1500;
  var BATCH = 20;
  var TYPES = ['viewed', 'added', 'dismissed', 'prompt_shown', 'prompt_clicked'];
  var SYSTEM_SKUS = ['BCRS-DEP', 'SVC-FEE'];

  function rand() {
    var s = '';
    try {
      if (root.crypto && root.crypto.getRandomValues) {
        var a = new Uint8Array(8); root.crypto.getRandomValues(a);
        for (var i = 0; i < a.length; i++) s += (a[i] + 256).toString(16).slice(1);
        return s;
      }
    } catch (e) { /* fall through */ }
    return Math.random().toString(16).slice(2, 10) + Math.random().toString(16).slice(2, 10);
  }
  /** "<time base36>-<random>": 8 to 128 of [A-Za-z0-9._:-], which is what the Brain accepts. */
  function newId() { return Date.now().toString(36) + '-' + rand(); }
  function validSession(s) { return typeof s === 'string' && /^[A-Za-z0-9._:-]{8,128}$/.test(s); }
  function isSystemSku(sku) { return SYSTEM_SKUS.indexOf(String(sku || '').trim().toUpperCase()) !== -1; }

  /** /cart.js → the scalars the Brain keeps: token, item count and drinks subtotal in cents, system lines out. */
  function cartSummary(cart) {
    var items = (cart && cart.items) || [];
    var count = 0, cents = 0;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it || isSystemSku(it.sku)) continue;
      var q = Math.max(0, Math.round(Number(it.quantity) || 0));
      count += q;
      cents += q * Math.max(0, Math.round(Number(it.price) || 0));
    }
    return { cart_token: cart && typeof cart.token === 'string' ? cart.token : null, item_count: count, drinks_subtotal_cents: cents };
  }

  var state = { session: null, queue: [], timer: null };

  function session() {
    if (state.session) return state.session;
    var s = null;
    try { s = root.sessionStorage.getItem(SESSION_KEY); } catch (e) { /* private mode */ }
    if (!validSession(s)) {
      s = 's-' + newId();
      try { root.sessionStorage.setItem(SESSION_KEY, s); } catch (e) { /* private mode */ }
    }
    state.session = s;
    return s;
  }

  function send(json) {
    try {
      if (root.navigator && typeof root.navigator.sendBeacon === 'function') {
        if (root.navigator.sendBeacon(ENDPOINT, new Blob([json], { type: 'application/json' }))) return;
      }
      if (typeof fetch === 'function') {
        fetch(ENDPOINT, { method: 'POST', body: json, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(function () {});
      }
    } catch (e) { /* the page is not the place to say so */ }
  }

  function flush() {
    if (state.timer) { clearTimeout(state.timer); state.timer = null; }
    while (state.queue.length) {
      var batch = state.queue.splice(0, BATCH);
      var json;
      try { json = JSON.stringify({ sessionId: session(), events: batch }); } catch (e) { continue; }
      send(json);
    }
  }

  function track(type, payload) {
    try {
      if (TYPES.indexOf(type) === -1) return false;
      var clean = {};
      if (payload && typeof payload === 'object') {
        for (var k in payload) {
          if (!Object.prototype.hasOwnProperty.call(payload, k)) continue;
          var v = payload[k];
          if (v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') clean[k] = v;
        }
      }
      state.queue.push({ type: type, id: newId(), occurredAt: new Date().toISOString(), payload: clean });
      if (!state.timer) state.timer = setTimeout(flush, FLUSH_MS);
      return true;
    } catch (e) { return false; }
  }

  function cartAdded(variantId) {
    try {
      var url = (root.routes && root.routes.cart_url) || '/cart';
      fetch(url + '.js', { cache: 'no-store', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var s = cartSummary(cart);
          if (!s.cart_token) return;
          s.variant_id = variantId || null;
          track('added', s);
        })
        .catch(function () {});
    } catch (e) { /* nothing to do */ }
  }

  function boot() {
    try {
      var me = document.currentScript || document.querySelector('script[src*="dn-events"]');
      var productId = me && me.getAttribute('data-product-id');
      if (productId) track('viewed', { product_id: Number(productId) || productId });

      if (typeof root.subscribe === 'function' && typeof root.PUB_SUB_EVENTS !== 'undefined' && root.PUB_SUB_EVENTS.cartUpdate) {
        root.subscribe(root.PUB_SUB_EVENTS.cartUpdate, function (ev) {
          if (ev && ev.source === 'dn-cart') return;
          cartAdded(ev && ev.productVariantId ? Number(ev.productVariantId) : null);
        });
      }
      document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });
      root.addEventListener('pagehide', flush);
    } catch (e) { /* the page must not notice */ }
  }

  return { track: track, flush: flush, boot: boot, cartSummary: cartSummary, newId: newId, validSession: validSession, isSystemSku: isSystemSku };
});
